/**
 * Domain errors with stable, documented codes.
 *
 * The code set is fixed by `docs/api-contracts.md` and is part of the public API contract: a
 * client may branch on a code, so renaming one is a breaking change. Messages are safe to show
 * to a donor — they never carry an identifier, an amount from another tenant, or provider detail.
 */
export const ERROR_CODES = [
    "invalid_context",
    "organization_not_approved",
    "capability_disabled",
    "quote_expired",
    "amount_changed",
    "authentication_required",
    "step_up_required",
    "forbidden",
    "tenant_mismatch",
    "provider_processing",
    "provider_unavailable",
    /**
     * The donor's bank declined a card charged in one step, a saved card (GH-945). The message is
     * the donor sentence for the specific reason; nothing was charged and another card may be tried.
     */
    "card_declined",
    "reconciliation_required",
    "idempotency_key_reuse",
    "invalid_request",
    "not_found",
    /**
     * The event exists and once faced the public, but has been archived (GH-863). Distinct from
     * `not_found` so a donor holding a link sees "This event has ended" rather than "could not be
     * found"; a suspended, draft or in-review event still answers `not_found`, so this code never
     * confirms a takedown or an unpublished event.
     */
    "event_unavailable",
    /**
     * An invite code that cannot be redeemed (GH-864). One answer for unknown, used, revoked and
     * expired, with the same timing class, so the response never tells a guesser which it was.
     */
    "invite_unavailable",
    /** Redeeming or joining when the donor is already a member (GH-864). */
    "already_member",
    /**
     * A member-only route (influence, issuing invites, credit-earning share codes) asked for by a
     * signed-in donor who is not a member (GH-864). Never raised on giving, receipts or staff routes.
     */
    "membership_required",
    /**
     * Signing up — the sign-in that would create a new account — without an invite code or a valid
     * big-screen join token, while membership is on (GH-871a, DEC-052). No account is created; the
     * client offers the account-less waitlist. An existing account never sees it.
     */
    "invite_required",
    /**
     * An event's page, totals or prompts asked for by someone who is neither a signed-in member nor
     * holding an access token for that event (big-screen QR or at-event location), while
     * membership is on (GH-871a, DEC-052). Tickets and giving never answer it.
     */
    "event_access_required",
    /**
     * Checking in to an event, or entering its door prize, asked for by a donor whose profile holds
     * no mobile number proved by an SMS code, on a deployment that can send one (GH-883). The client
     * runs the existing add-a-number flow (`/v1/donor/phone`, then `/confirm`) and tries again.
     * Never raised on giving, receipts, tickets or staff routes, and never where codes cannot be sent.
     */
    "phone_verification_required",
    /**
     * Cancelling an end-of-day gift whose card was already captured (DEC-056, GH-947). Nothing was
     * withdrawn; the money was taken, so the console offers a refund instead.
     */
    "gift_already_captured",
    /**
     * The organization turned refunds off (DEC-058, GH-951): a console gift or ticket refund, a
     * held refund, or the approval of one already held. Nothing moved. An owner or admin turns them
     * back on in Org info; a cancel of an uncaptured authorization is never refused with it.
     */
    "refunds_disabled",
    /**
     * A platform administrator signed in, or stepped up, with a Google account outside the Workspace
     * domains the deployment pins platform staff to (GAP-01, `PLATFORM_STAFF_HOSTED_DOMAINS`).
     * Nothing was issued; the message never names the expected domain. The person signs in again
     * with their managed Workspace account. Organization staff never see it.
     */
    "platform_staff_domain_required",
    "conflict",
    "illegal_state_transition",
    "rate_limited",
    "internal_error",
];
export class DomainError extends Error {
    name = "DomainError";
    code;
    retryability;
    details;
    constructor(code, message, options = {}) {
        super(message, options.cause === undefined ? undefined : { cause: options.cause });
        this.code = code;
        this.retryability = options.retryability ?? "not_retryable";
        this.details = options.details ?? {};
    }
}
export const HTTP_STATUS_BY_CODE = {
    invalid_context: 400,
    organization_not_approved: 409,
    capability_disabled: 409,
    quote_expired: 409,
    amount_changed: 409,
    authentication_required: 401,
    step_up_required: 401,
    forbidden: 403,
    tenant_mismatch: 403,
    provider_processing: 202,
    provider_unavailable: 503,
    card_declined: 402,
    reconciliation_required: 409,
    idempotency_key_reuse: 409,
    invalid_request: 400,
    not_found: 404,
    event_unavailable: 410,
    invite_unavailable: 409,
    already_member: 409,
    membership_required: 403,
    invite_required: 403,
    event_access_required: 403,
    phone_verification_required: 403,
    gift_already_captured: 409,
    refunds_disabled: 409,
    platform_staff_domain_required: 403,
    conflict: 409,
    illegal_state_transition: 409,
    rate_limited: 429,
    internal_error: 500,
};
export const isDomainError = (error) => error instanceof DomainError;
/**
 * `tenant_mismatch` deliberately exists as a distinct code from `forbidden` so cross-tenant
 * attempts are separable in logs and alerts. A spike in one means something very different from
 * a spike in the other.
 */
export function tenantMismatch(message = "Resource belongs to a different organization") {
    return new DomainError("tenant_mismatch", message);
}
export function notFound(what) {
    return new DomainError("not_found", `${what} not found`);
}
export function invalidRequest(message, details) {
    return new DomainError("invalid_request", message, details ? { details } : {});
}
//# sourceMappingURL=errors.js.map